import logo from "@/assets/logo-idaco.webp";
import { Phone, Mail, MapPin } from "lucide-react";

const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-br from-burgundy-dark via-burgundy to-burgundy-dark text-cream overflow-hidden">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(255,215,0,0.1)_0%,transparent_70%)]" />
      </div>
      
      {/* Ornamental top border */}
      <div className="w-full h-1 bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative py-16 sm:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12 lg:gap-16 mb-12 sm:mb-16">
          {/* Idaco Romania */}
          <div className="space-y-4">
            <img 
              src={logo} 
              alt="Idaco Logo" 
              className="h-16 sm:h-20 w-auto mb-6 brightness-0 invert opacity-90 hover:opacity-100 transition-opacity"
            />
            <h4 className="font-bold text-xl sm:text-2xl mb-6 text-gold font-playfair relative inline-block">
              Idaco Romania
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gold"></span>
            </h4>
            <div className="space-y-3 text-cream/90 text-sm sm:text-base font-lora leading-relaxed">
              <p className="font-semibold">S.C. IDACO S.R.L.</p>
              <p className="text-cream/75">CIF: RO14053218</p>
              <p className="text-cream/75">Nr. Reg. Comerțului: J23/501/2001</p>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-bold text-xl sm:text-2xl mb-6 text-gold font-playfair relative inline-block">
              Contact
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gold"></span>
            </h4>
            <ul className="space-y-4">
              <li className="text-cream/90 text-sm sm:text-base font-lora leading-relaxed">
                <span className="font-semibold text-gold block mb-1">Adresa:</span>
                <span className="text-cream/75">Intrarea Tufanului nr. 7, Otopeni, 075100, România</span>
              </li>
              <li>
                <a href="tel:+40722637470" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora block group">
                  <span className="font-semibold text-gold block mb-1">Telefon:</span>
                  <span className="text-cream/75 group-hover:translate-x-1 inline-block transition-transform">+40722.637.470</span>
                </a>
              </li>
              <li>
                <a href="mailto:idaco_traditional@yahoo.com" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora block group">
                  <span className="font-semibold text-gold block mb-1">Email:</span>
                  <span className="text-cream/75 group-hover:translate-x-1 inline-block transition-transform break-all">idaco_traditional@yahoo.com</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Categorii produse */}
          <div className="space-y-4">
            <h4 className="font-bold text-xl sm:text-2xl mb-6 text-gold font-playfair relative inline-block">
              Categorii produse
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gold"></span>
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/magazin?category=carte-religioasa-legata-in-piele" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Cărți religioase legate în piele</span>
                </a>
              </li>
              <li>
                <a href="/magazin?category=suvenir-religios" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Suvenir religios</span>
                </a>
              </li>
              <li>
                <a href="/magazin?category=cadouri-unicat" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Cadouri unicat</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Informații utile */}
          <div className="space-y-4">
            <h4 className="font-bold text-xl sm:text-2xl mb-6 text-gold font-playfair relative inline-block">
              Informații utile
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-gold"></span>
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="/termeni-conditii" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Termeni și condiții</span>
                </a>
              </li>
              <li>
                <a href="/politica-confidentialitate" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Politica de confidențialitate</span>
                </a>
              </li>
              <li>
                <a href="/politica-cookies" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">Preferințe cookie</span>
                </a>
              </li>
              <li>
                <a href="https://anpc.ro" target="_blank" rel="noopener noreferrer" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">ANPC</span>
                </a>
              </li>
              <li>
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-cream/90 hover:text-gold transition-all duration-300 text-sm sm:text-base font-lora group flex items-center">
                  <span className="w-1.5 h-1.5 bg-gold rounded-full mr-3 opacity-60 group-hover:opacity-100 transition-opacity"></span>
                  <span className="group-hover:translate-x-1 inline-block transition-transform">SOL</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom section with ornamental border */}
        <div className="relative mt-12 pt-8">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"></div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <p className="text-cream/80 font-lora text-sm sm:text-base">
              © 2025 Idaco. Toate drepturile rezervate.
            </p>
            <p className="text-cream/70 font-lora text-xs sm:text-sm text-center md:text-right italic">
              Designed with love by{" "}
              <a 
                href="https://robert.krigalabs.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold hover:text-cream transition-colors duration-300 font-semibold"
              >
                RD
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
